<template>
    <div class="corporate-container">
        <!-- Page Header -->
        <div class="page-header">
            <div class="header-content">
                <div class="header-left">
                    <h1 class="page-title">Corporate Management</h1>
                    <p class="page-subtitle">Manage all corporate tenants and their configurations</p>
                </div>
                <div class="header-right">
                    <router-link :to="{ name: 'ExtranetCorporateForm' }" class="btn btn-primary">
                        <i class="fas fa-plus me-2"></i>
                        Add Corporate
                    </router-link>
                </div>
            </div>
        </div>

        <!-- Main Card Container -->
        <div class="management-card">
            <!-- Filter & Search Bar - Modern Transaction Style -->
            <div class="filter-bar">
                <div class="filter-left">
                    <div class="search-box-custom">
                        <div class="search-type-tabs">
                            <button 
                                v-for="option in searchOptions" 
                                :key="option.value"
                                class="tab-btn"
                                :class="{ active: searchBy.selected === option.value }"
                                @click="selectSearchType(option.value)"
                            >
                                {{ option.text }}
                            </button>
                        </div>
                        <div class="search-input-wrapper">
                            <input 
                                type="text" 
                                class="form-control-search" 
                                v-model="searchBy.criteria" 
                                placeholder="Search..." 
                                @keyup.enter="search()" 
                            />
                            <button class="btn-search" @click="search()">
                                <i class="fas fa-search"></i>
                            </button>
                        </div>
                    </div>

                    <div class="status-filter">
                        <div class="section-label-tabs">
                            <span class="label-tab">Status</span>
                        </div>
                        <div class="search-type-tabs">
                            <button 
                                class="tab-btn"
                                :class="{ active: !searchBy.status }"
                                @click="searchBy.status = ''; search()"
                            >
                                All
                            </button>
                            <button 
                                v-for="status in statusOptions" 
                                :key="status.value"
                                class="tab-btn"
                                :class="{ active: searchBy.status === status.value }"
                                @click="searchBy.status = status.value; search()"
                            >
                                {{ status.text }}
                            </button>
                        </div>
                    </div>
                </div>
                
                <div class="filter-right">
                    <div class="stats-summary">
                        <span class="stats-label">Total Results:</span>
                        <span class="stats-value">{{ meta.total }}</span>
                    </div>
                    <button class="btn-refresh" @click.prevent="loadData()" :disabled="page.isLoading" title="Refresh Data">
                        <i class="fas fa-sync-alt" :class="{ 'fa-spin': page.isLoading }"></i>
                    </button>
                </div>
            </div>

            <!-- Content Area -->
            <div class="card-content">
                <!-- Loading State -->
                <div v-if="page.isLoading && !page.output" class="loading-state">
                    <div class="loader-box">
                        <div class="spinner-border text-danger" role="status"></div>
                        <p>Fetching corporates...</p>
                    </div>
                </div>

                <!-- Table Content -->
                <div v-else-if="page.output && isMounted" class="table-outer">
                    <div class="table-responsive">
                        <table class="modern-list-table">
                            <thead>
                                <tr v-if="fields">
                                    <th v-for="(field, index) in fields" :key="index">
                                        {{ field }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in corporates" :key="item.id">
                                    <td class="col-no">{{ (meta.page - 1) * meta.limit + index + 1 }}</td>
                                    <td class="col-code">
                                        <span class="code-tag">{{ item.code }}</span>
                                    </td>
                                    <td class="col-name">
                                        <div class="name-box">
                                            <div class="name-icon">
                                                <i class="fas fa-building"></i>
                                            </div>
                                            <span class="name-text">{{ item.name }}</span>
                                        </div>
                                    </td>
                                    <td class="col-domain">
                                        <div class="domain-wrapper">
                                            <template v-if="item.domains && item.domains.length > 0">
                                                <a 
                                                    v-for="(domain, idx) in item.domains" 
                                                    :key="idx" 
                                                    :href="`http://${domain}`" 
                                                    target="_blank"
                                                    class="domain-pill"
                                                >
                                                    {{ domain }}
                                                    <i class="fas fa-external-link-alt"></i>
                                                </a>
                                            </template>
                                            <span v-else class="empty-dash">-</span>
                                        </div>
                                    </td>
                                    <td class="col-status">
                                        <span 
                                            class="status-pill" 
                                            :class="item.status">
                                            <span class="status-dot"></span>
                                            {{ item.status }}
                                        </span>
                                    </td>
                                    <td class="col-date">{{ formatDate(item.created_at) }}</td>
                                    <td class="col-actions">
                                        <div class="action-group">
                                            <router-link 
                                                :to="{ name: 'ExtranetCorporateEdit', params: { id: item.id } }"
                                                class="action-btn edit"
                                                title="Edit Corporate"
                                            >
                                                <i class="fas fa-edit"></i>
                                            </router-link>
                                            <button 
                                                class="action-btn delete"
                                                @click.prevent="destroy(item.id)"
                                                title="Delete Corporate"
                                            >
                                                <i class="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                
                                <!-- Empty State -->
                                <tr v-if="corporates.length === 0">
                                    <td colspan="7">
                                        <div class="empty-list">
                                            <div class="empty-art">
                                                <i class="fas fa-building"></i>
                                            </div>
                                            <h3>No Corporates Found</h3>
                                            <p>Tailor your search or create a new corporate to get started.</p>
                                            <router-link :to="{ name: 'ExtranetCorporateForm' }" class="btn btn-primary mt-3">
                                                <i class="fas fa-plus me-2"></i> Add Corporate
                                            </router-link>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- Footer / Pagination -->
            <div class="card-footer" v-if="page.output && corporates.length > 0">
                <o-pagination 
                    :page="meta.page"  
                    :total="meta.last_page" 
                    @callback="navPage"/>
            </div>
        </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <o-warning-box
      v-if="showDeleteModal"
      title="Delete Corporate"
      :item-name="corporateToDelete?.name || 'this corporate'"
      warning-message="This action cannot be undone and will remove all associated tenant data."
      confirm-text="Delete Corporate"
      cancel-text="Keep it"
      @confirm="confirmDelete"
      @cancel="showDeleteModal = false"
    />
</template>

<script>
import { 
    onMounted, 
    reactive, 
    ref,
    onBeforeUnmount,
} from 'vue'
import api from '@/helpers/api'
import { useToast } from '@/composables/useToast'
import {
    OPagination,
    OWarningBox
} from '@/components/OmnihControl'

export default {
    name: 'ExtranetCorporate',
    
    setup() {
        const { showToast } = useToast()
        const isMounted = ref(true)

        const page = reactive({
            isLoading: false,
            output: false
        })

        const fields = ref([
            'No',
            'Code',
            'Corporate Name',
            'Primary Domain',
            'Status',
            'Join Date',
            'Actions'
        ])

        const corporates = ref([])
        
        const meta = reactive({
            page: 1,
            limit: 10,
            total: 0,
            last_page: 1
        })

        const searchOptions = reactive([
            { value: 'code', text: 'Code' },
            { value: 'name', text: 'Name' },
        ])

        const statusOptions = reactive([
            { value: 'active', text: 'Active' },
            { value: 'inactive', text: 'Inactive' },
        ])

        const searchBy = reactive({
            selected: 'name',
            criteria: '',
            status: ''
        })

        const showDeleteModal = ref(false)
        const corporateToDelete = ref(null)

        function formatDate(dateString) {
            if (!dateString) return '-'
            const date = new Date(dateString)
            return date.toLocaleDateString('en-GB', { 
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            })
        }

        async function loadData() {
            if (!isMounted.value) return
            page.isLoading = true
            try {
                const params = {
                    page: meta.page,
                    per_page: meta.limit
                }
                
                if (searchBy.criteria) {
                    params[searchBy.selected] = searchBy.criteria
                }
                
                if (searchBy.status) {
                    params.status = searchBy.status
                }

                const response = await api.request('get', '/api/extranet/corporate', { custom: params })
                if (!isMounted.value) return
                if (response.data && response.data.data) {
                    const rawData = response.data.data.data || response.data.data
                    corporates.value = rawData.map(item => ({
                        id: item.id,
                        code: item.code,
                        name: item.name,
                        domains: item.tenant_domain ? [item.tenant_domain] : [],
                        status: item.register_option?.status === 1 ? 'active' : 'inactive',
                        created_at: item.created_at,
                    }))
                    if (response.data.data.meta) {
                        meta.page = response.data.data.meta.current_page || 1
                        meta.limit = response.data.data.meta.per_page || 10
                        meta.total = response.data.data.meta.total || 0
                        meta.last_page = response.data.data.meta.last_page || Math.ceil(meta.total / meta.limit) || 1
                    } else {
                        meta.total = corporates.value.length
                        meta.last_page = 1
                    }
                }
                page.output = true
            } catch (error) {
                if (!isMounted.value) return
                showToast({
                    type: 'error',
                    title: 'Fetch Error',
                    message: error.response?.data?.message || 'Failed to sync corporates.',
                    duration: 4000
                })
            } finally {
                if (isMounted.value) page.isLoading = false
            }
        }

        function selectSearchType(type) {
            searchBy.selected = type
            if (searchBy.criteria) search()
        }

        function destroy(id) {
            const corporate = corporates.value.find(c => c.id === id)
            if (corporate) {
                corporateToDelete.value = corporate
                showDeleteModal.value = true
            }
        }

        async function confirmDelete() {
            if (!corporateToDelete.value || !isMounted.value) return
            page.isLoading = true
            showDeleteModal.value = false
            try {
                await api.request('delete', `/api/extranet/corporate/${corporateToDelete.value.id}`)
                if (!isMounted.value) return
                showToast({
                    type: 'success',
                    title: 'Deleted',
                    message: 'Corporate and associated data removed.',
                    duration: 4000
                })
                await loadData()
            } catch (error) {
                if (!isMounted.value) return
                showToast({
                    type: 'error',
                    title: 'Delete Failed',
                    message: error.response?.data?.message || 'Record could not be removed.',
                    duration: 4000
                })
            } finally {
                if (isMounted.value) {
                    page.isLoading = false
                    corporateToDelete.value = null
                }
            }
        }

        function navPage(newPage) {
            meta.page = newPage
            loadData()
        }

        function search() {
            meta.page = 1
            loadData()
        }

        function clearSearch() {
            searchBy.criteria = ''
            searchBy.status = ''
            meta.page = 1
            loadData()
        }

        onMounted(() => {
            isMounted.value = true
            loadData()
        })

        onBeforeUnmount(() => { isMounted.value = false })

        return {
            page, fields, corporates, meta, searchOptions, statusOptions, searchBy,
            showDeleteModal, corporateToDelete, isMounted,
            formatDate, loadData, destroy, confirmDelete, navPage, search, clearSearch, selectSearchType
        }
    },

    components: {
        OPagination, OWarningBox
    }
}
</script>

<style scoped>
.corporate-container {
    max-width: 1400px;
    margin: 0 auto;
}

/* Page Header */
.page-header {
    margin-bottom: 32px;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.page-title {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 4px;
}

.page-subtitle {
    font-size: 14px;
    color: #64748b;
    margin: 0;
}

/* Management Card */
.management-card {
    background: white;
    border-radius: var(--extranet-rounded);
    border: 1px solid var(--extranet-accent);
    overflow: hidden;
}

/* Filter Bar - Modern Style */
.filter-bar {
    padding: 24px 32px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    background: #ffffff;
    border-bottom: 1px solid #f1f5f9;
    gap: 24px;
}

.filter-left {
    display: flex;
    align-items: flex-end;
    gap: 24px;
    flex: 1;
}

.search-box-custom {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 0 1 400px;
}

.status-filter {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.section-label-tabs {
    display: flex;
}

.label-tab {
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.search-type-tabs {
    display: flex;
    gap: 4px;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 10px;
    width: fit-content;
}

.tab-btn {
    border: none;
    background: transparent;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
    border-radius: 8px;
    transition: all 0.2s;
    cursor: pointer;
}

.tab-btn.active {
    background: white;
    color: var(--extranet-primary);
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.search-input-wrapper {
    display: flex;
    align-items: center;
    background: #f8fafc;
    border: 1px solid var(--extranet-accent);
    border-radius: 12px;
    padding: 4px;
    transition: all 0.2s;
}

.search-input-wrapper:focus-within {
    border-color: var(--extranet-primary);
    background: white;
    box-shadow: 0 0 0 3px rgba(181, 26, 31, 0.05);
}

.form-control-search {
    border: none;
    background: transparent;
    padding: 8px 12px;
    font-size: 14px;
    color: #1e293b;
    width: 100%;
    outline: none;
}

.btn-search {
    background: white;
    border: 1px solid var(--extranet-accent);
    width: 36px;
    height: 36px;
    border-radius: 10px;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.btn-search:hover {
    color: var(--extranet-primary);
    border-color: var(--extranet-primary);
}

.filter-right {
    display: flex;
    align-items: center;
    gap: 16px;
}

.stats-summary {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: #f8fafc;
    border-radius: 12px;
    font-size: 13px;
    border: 1px solid var(--extranet-accent);
}

.stats-label {
    color: #64748b;
    font-weight: 500;
}

.stats-value {
    color: #0f172a;
    font-weight: 700;
}

.btn-refresh {
    width: 42px;
    height: 42px;
    border: 1px solid var(--extranet-accent);
    background: white;
    border-radius: 12px;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-refresh:hover:not(:disabled) {
    border-color: var(--extranet-primary);
    color: var(--extranet-primary);
    background: #fff5f5;
}

/* Table Area */
.card-content {
    min-height: 400px;
}

.table-outer {
    width: 100%;
}

.modern-list-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.modern-list-table thead th {
    padding: 16px 32px;
    background: #f8fafc;
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    border-bottom: 1px solid var(--extranet-accent);
    text-align: left;
}

.modern-list-table tbody td {
    padding: 20px 32px;
    vertical-align: middle;
    border-bottom: 1px solid var(--extranet-accent);
    color: #334155;
    font-size: 14px;
}

.modern-list-table tbody tr:hover {
    background: #fffafa;
}

.col-no {
    width: 60px;
    color: #94a3b8 !important;
    font-weight: 500;
}

.code-tag {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    font-weight: 600;
    color: #1e293b;
    background: #f1f5f9;
    padding: 4px 10px;
    border-radius: 6px;
}

.name-box {
    display: flex;
    align-items: center;
    gap: 12px;
}

.name-icon {
    width: 32px;
    height: 32px;
    background: #fff5f5;
    color: var(--extranet-primary);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
}

.name-text {
    font-weight: 600;
}

.domain-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.domain-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    background: #f1f5f9;
    color: #64748b;
    border-radius: var(--extranet-rounded);
    font-size: 12px;
    text-decoration: none;
    transition: all 0.2s;
    border: 1px solid transparent;
}

.domain-pill:hover {
    background: white;
    border-color: var(--extranet-primary);
    color: var(--extranet-primary);
}

.status-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border-radius: var(--extranet-rounded);
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
}

.status-pill.active {
    background: #ecfdf5;
    color: #059669;
}

.status-pill.inactive {
    background: #f1f5f9;
    color: #64748b;
}

.status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
}

.action-group {
    display: flex;
    gap: 8px;
}

.action-btn {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--extranet-accent);
    background: white;
    color: #64748b;
    transition: all 0.2s;
    cursor: pointer;
    text-decoration: none;
}

.action-btn.edit:hover {
    background: #eff6ff;
    color: #2563eb;
    border-color: #2563eb;
}

.action-btn.delete:hover {
    background: #fef2f2;
    color: #dc2626;
    border-color: #dc2626;
}

/* Empty State */
.empty-list {
    padding: 80px 32px;
    text-align: center;
}

.empty-art {
    font-size: 48px;
    color: #e2e8f0;
    margin-bottom: 20px;
}

.empty-list h3 {
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;
}

.empty-list p {
    color: #94a3b8;
    font-size: 14px;
}

/* Loading State */
.loading-state {
    padding: 100px 32px;
    display: flex;
    justify-content: center;
}

/* Footer & Pagination */
.card-footer {
    padding: 24px 32px;
    background: #ffffff;
    border-top: 1px solid #f1f5f9;
}

/* General Buttons */
.btn {
    padding: 12px 24px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 700;
    border: none;
    transition: all 0.2s;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
}

.btn-primary {
    background: var(--extranet-primary);
    color: white;
}

.btn-primary:hover {
    filter: brightness(0.9);
    transform: translateY(-1px);
}

/* Responsive */
@media (max-width: 1200px) {
    .filter-bar {
        flex-direction: column;
        align-items: stretch;
    }
    .filter-left {
        flex-direction: column;
        align-items: stretch;
    }
    .search-box-custom {
        flex: 1;
    }
}

@media (max-width: 768px) {
    .header-content {
        flex-direction: column;
        align-items: flex-start;
        gap: 20px;
    }
    
    .header-right, .header-right .btn {
        width: 100%;
        justify-content: center;
    }
}
</style>
